import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Save, 
  X,
  Package,
  DollarSign,
  Tag,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  serverTimestamp
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useRealtime } from '../../contexts/RealtimeContext';

const StoreManagement = ({ coachingId }) => {
  const { userProfile } = useRealtime();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Permission check for staff
  const isStaff = userProfile?.role === 'coaching_staff';
  const hasEditAccess = !isStaff || userProfile?.accessibleSections?.includes('store');

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Other',
    price: '',
    description: '',
    sizes: [],
    inStock: true,
    quantityInStock: ''
  });

  const categories = [
    'Shirt',
    'Pant',
    'Book',
    'Tie',
    'Uniform',
    'Stationery',
    'Other'
  ];

  const sizeOptions = {
    'Shirt': ['S', 'M', 'L', 'XL', 'XXL', '3XL'],
    'Pant': ['28', '30', '32', '34', '36', '38', '40'],
    'Uniform': ['Small', 'Medium', 'Large', 'Extra Large'],
    'Other': []
  };

  useEffect(() => {
    loadStoreItems();
  }, [coachingId]);

  const loadStoreItems = async () => {
    if (!coachingId || coachingId === 'default-coaching-id') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const storeRef = collection(db, COLLECTIONS.STORE_ITEMS || 'storeItems');
      const q = query(storeRef, where('coachingId', '==', coachingId));
      const snapshot = await getDocs(q);
      
      const itemsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setItems(itemsData);
    } catch (error) {
      console.error('Error loading store items:', error);
      toast.error('Failed to load store items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    if (!coachingId || coachingId === 'default-coaching-id') {
      toast.error('Cannot add item: Coaching ID is missing. Please refresh the page.');
      return;
    }

    try {
      const storeRef = collection(db, COLLECTIONS.STORE_ITEMS || 'storeItems');
      const quantity = parseInt(formData.quantityInStock) || 0;
      const itemData = {
        ...formData,
        price: parseFloat(formData.price),
        quantityInStock: quantity,
        inStock: quantity > 0, // Automatically set based on quantity
        coachingId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('Attempting to add item:', itemData);
      const docRef = await addDoc(storeRef, itemData);
      console.log('Successfully added item with ID:', docRef.id);

      toast.success('Item added to store successfully');
      setShowAddModal(false);
      resetForm();
      loadStoreItems();
    } catch (error) {
      console.error('Full error details for adding store item:', error);
      toast.error(`Failed to add item: ${error.message || 'Unknown error'}`);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedItem) return;

    try {
      const itemRef = doc(db, COLLECTIONS.STORE_ITEMS || 'storeItems', selectedItem.id);
      const quantity = parseInt(formData.quantityInStock) || 0;
      const updateData = {
        ...formData,
        price: parseFloat(formData.price),
        quantityInStock: quantity,
        inStock: quantity > 0, // Automatically set based on quantity
        updatedAt: serverTimestamp()
      };

      console.log('Attempting to update item:', selectedItem.id, updateData);
      await updateDoc(itemRef, updateData);
      console.log('Successfully updated item:', selectedItem.id);

      toast.success('Item updated successfully');
      setShowEditModal(false);
      setSelectedItem(null);
      resetForm();
      loadStoreItems();
    } catch (error) {
      console.error('Full error details for updating store item:', error);
      toast.error(`Failed to update item: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const itemRef = doc(db, COLLECTIONS.STORE_ITEMS || 'storeItems', itemId);
      await deleteDoc(itemRef);
      toast.success('Item deleted successfully');
      loadStoreItems();
    } catch (error) {
      console.error('Error deleting store item:', error);
      toast.error(`Failed to delete item: ${error.message || 'Unknown error'}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Other',
      price: '',
      description: '',
      sizes: [],
      inStock: true,
      quantityInStock: ''
    });
  };

  const handleSizeToggle = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: [size] // User can only select one size
    }));
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      {/* Header Section */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShoppingBag className="text-blue-600" />
              Store Management
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Manage school uniforms, books, and other products
            </p>
          </div>
          {hasEditAccess && (
            <button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
            >
              <Plus size={20} />
              Add New Item
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mt-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search items by name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
          />
        </div>
      </div>

      {/* Items Grid */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <div 
                key={item.id}
                className={`group relative border rounded-xl p-4 hover:shadow-md transition-all ${
                  item.quantityInStock === 0 
                    ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800/50' 
                    : item.quantityInStock < 10 
                    ? 'bg-orange-50/50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800/50' 
                    : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${
                    item.quantityInStock === 0
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30'
                      : item.quantityInStock < 10
                      ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30'
                      : item.category === 'Shirt' || item.category === 'Pant' || item.category === 'Uniform'
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30'
                      : item.category === 'Book'
                      ? 'bg-green-100 text-green-600 dark:bg-green-900/30'
                      : 'bg-purple-100 text-purple-600 dark:bg-purple-900/30'
                  }`}>
                    <Package size={24} />
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {item.quantityInStock === 0 ? (
                      <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                        Out of Stock
                      </span>
                    ) : item.quantityInStock < 10 ? (
                      <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                        Low Stock
                      </span>
                    ) : null}
                    
                    <div className={`flex gap-2 transition-opacity ${hasEditAccess ? 'opacity-0 group-hover:opacity-100' : 'hidden'}`}>
                      <button
                        onClick={() => {
                          setSelectedItem(item);
                          setFormData({
                            name: item.name,
                            category: item.category,
                            price: item.price.toString(),
                            description: item.description || '',
                            sizes: item.sizes || [],
                            inStock: item.inStock ?? true,
                            quantityInStock: item.quantityInStock?.toString() || ''
                          });
                          setShowEditModal(true);
                        }}
                        className="p-1.5 bg-white dark:bg-gray-800 text-blue-600 hover:text-blue-700 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-1.5 bg-white dark:bg-gray-800 text-red-600 hover:text-red-700 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <h3 className="font-bold text-gray-900 dark:text-white truncate">{item.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 min-h-[2.5rem]">
                  {item.description || 'No description available'}
                </p>

                <div className="mt-4 flex items-center justify-between">
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    ₹{item.price.toLocaleString()}
                  </span>
                  <div className="flex flex-col items-end">
                    <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                      {item.category}
                    </span>
                    {item.quantityInStock !== undefined && (
                      <span className={`text-[10px] mt-1 font-bold ${
                        item.quantityInStock === 0 
                          ? 'text-red-600 dark:text-red-400' 
                          : item.quantityInStock < 10 
                          ? 'text-orange-600 dark:text-orange-400' 
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        Stock: {item.quantityInStock}
                      </span>
                    )}
                  </div>
                </div>

                {item.sizes && item.sizes.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {item.sizes.map(size => (
                      <span key={size} className="text-[10px] px-1.5 py-0.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded">
                        {size}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <ShoppingBag className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={64} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No items found</h3>
            <p className="text-gray-500 dark:text-gray-400">Try adding some products to your store.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {showAddModal ? 'Add New Store Item' : 'Edit Store Item'}
              </h3>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Item Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Tag size={16} /> Item Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g. School Shirt, Math Book"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Package size={16} /> Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value, sizes: []})}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <DollarSign size={16} /> Price (₹)
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                  />
                </div>

                {/* Quantity In Stock */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Package size={16} /> Quantity in Stock
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.quantityInStock}
                    onChange={(e) => setFormData({...formData, quantityInStock: e.target.value})}
                    placeholder="0"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                  />
                </div>

                {/* In Stock Toggle */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <CheckCircle size={16} /> Status
                  </label>
                  <div className="flex items-center gap-4 py-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={formData.inStock}
                        onChange={(e) => setFormData({...formData, inStock: e.target.checked})}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {formData.inStock ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Additional details about the product..."
                  rows="3"
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white resize-none"
                />
              </div>

              {/* Size Options (Conditional) */}
              {sizeOptions[formData.category] && sizeOptions[formData.category].length > 0 && (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Size (One size per item)</label>
                  <div className="flex flex-wrap gap-2">
                    {sizeOptions[formData.category].map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handleSizeToggle(size)}
                        className={`px-4 py-2 rounded-lg border transition-all ${
                          formData.sizes.includes(size)
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-500'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  {showAddModal ? 'Add Item' : 'Update Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreManagement;
